import { Button, Input, InputElement, LoadingSpinner } from "../../../common/components";
import { faMapMarkerAlt, faSearch } from "@fortawesome/free-solid-svg-icons";
import { marginBottom, marginLeft, marginTop, media, paddingLeft } from "../../../common/styles";
import React, { ReactElement, useContext, useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { AppContext } from "../../../AppDataProvider/AppDataProvider";
import { emitEvent, loadExternalScript } from "../../../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
type Coordinates = {
  latitude: number;
  longitude: number;
};
export type OnFoundLocationOpts = {
  coordinates: Coordinates;
  searchStr: string;
};
type SearchProps = {
  onFoundLocation: (options: OnFoundLocationOpts) => void;
  sessionLocation?: string;
};
const prefilledLocationSearch = new URLSearchParams(window.location.search).get("locationSearchPrefill");
export function Search(options: SearchProps): ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const theme = useTheme();
  const {
    sessionLocation,
    onFoundLocation
  } = options;
  const {
    appData
  } = useContext(AppContext);
  const [userLocationLoading, setUserLocationLoading] = useState(false);
  const [textSearchLoading, setTextSearchLoading] = useState(false);
  const [locationText, setLocationText] = useState(sessionLocation ?? prefilledLocationSearch ?? "");
  const [error, setError] = useState(false);
  const {
    mapkitAuthenticationToken,
    locationSelection
  } = appData.bookingTool;
  useEffect(() => {
    loadExternalScript("https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js").then(() => {
      mapkit.init({
        authorizationCallback: done => {
          done(mapkitAuthenticationToken);
          // If the location is prefilled. Perform an immediate search after
          // mapkit has authenticated without requiring the user to click.
          if (prefilledLocationSearch) {
            searchLocation();
          }
        },
        language: i18n.language
      });
    }).catch(console.error);

    // Mapkit initialisation only needs to happen once, additional calls
    // just lead to warnings in the console.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const searchLocation = () => {
    setTextSearchLoading(true);
    setError(false);
    const geocoder = new mapkit.Geocoder({
      getsUserLocation: false,
      language: i18n.language
    });
    let foundMappedOverride;
    if (locationSelection) {
      const {
        overrideLocationSearchQueryMapping
      } = locationSelection;
      if (overrideLocationSearchQueryMapping && overrideLocationSearchQueryMapping.length > 0) {
        foundMappedOverride = overrideLocationSearchQueryMapping.find(override => override.from === locationText);
      }
    }
    const includedCountries = locationSelection && locationSelection.includedCountries ? locationSelection.includedCountries : [];

    /**
     * if we have a mapped override value,
     * we want to use that instead of the provided value.
     */
    const lookupValue = foundMappedOverride ? foundMappedOverride.to : locationText;
    const geoCoderOptions = {
      ...(includedCountries.length > 0 && {
        limitToCountries: includedCountries.join(",")
      })
    };
    geocoder.lookup(lookupValue, (error, response) => {
      if (error || response.results.length === 0) {
        console.error(error);
        setError(true);
      } else if (response.results.length > 0) {
        onFoundLocation({
          coordinates: response.results[0].coordinate,
          searchStr: locationText
        });
      }
      setTextSearchLoading(false);
    }, geoCoderOptions);
    if (appData.iFrameId) {
      emitEvent({
        data: {
          iFrameId: appData.iFrameId,
          location: lookupValue
        },
        name: "LOCATION_SEARCHED"
      });
    }
  };
  const detectLocation = () => {
    setError(false);
    setUserLocationLoading(true);
    navigator.geolocation.getCurrentPosition(position => {
      setUserLocationLoading(false);
      onFoundLocation({
        coordinates: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        },
        searchStr: locationText
      });
    }, error => {
      console.error(error);
      setError(true);
      setUserLocationLoading(false);
    }, {
      enableHighAccuracy: true,
      maximumAge: Infinity,
      timeout: 10000
    });
  };
  return <Container data-sentry-element="Container" data-sentry-component="Search" data-sentry-source-file="Search.tsx">
      <Form id="organisationSearchForm" aria-label={t("organisationSearchForm")} onSubmit={searchLocation} data-sentry-element="Form" data-sentry-source-file="Search.tsx">
        <LocationInputContainer data-sentry-element="LocationInputContainer" data-sentry-source-file="Search.tsx">
          <LocationInput value={locationText} multiple={false} label={t("findLocation")} aria-label={t("locationSearchText")} placeholder={t("locationSearchText")} width={"1000px"} onChange={(event: React.ChangeEvent<InputElement>) => {
          setLocationText(event.target.value);
        }} onKeyPress={(event: React.KeyboardEvent) => {
          if (event.key === "Enter") {
            event.preventDefault();
            searchLocation();
          }
        }} data-sentry-element="LocationInput" data-sentry-source-file="Search.tsx" />
          <SearchIcon icon={faSearch} data-sentry-element="SearchIcon" data-sentry-source-file="Search.tsx" />
        </LocationInputContainer>

        <UseLocationButton aria-label={t("useCurrentLocation")} onClick={event => {
        event.preventDefault();
        detectLocation();
      }} data-sentry-element="UseLocationButton" data-sentry-source-file="Search.tsx">
          {userLocationLoading ? <LoadingSpinner colour={theme.colours.textOnPrimary} /> : <UseLocationIcon icon={faMapMarkerAlt} />}
        </UseLocationButton>
      </Form>

      <SearchButton variant="primary" onClick={searchLocation} data-sentry-element="SearchButton" data-sentry-source-file="Search.tsx">
        {textSearchLoading ? <LoadingSpinner colour={theme.colours.textOnPrimary} /> : t("search")}
      </SearchButton>

      {!userLocationLoading && !textSearchLoading && error && <NoResultMessage>{t("noLocationResult")}</NoResultMessage>}
    </Container>;
}
export default Search;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  align-items: center;

  @media ${media.desktop} {
    width: 25rem;
  }
`;
const Form = styled.form`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  width: 100%;
`;
const LocationInputContainer = styled.div`
  position: relative;
  width: 100%;
`;
const LocationInput = styled(Input).attrs({
  variant: "text"
})`
  ${paddingLeft.l}
`;
const UseLocationButton = styled(Button).attrs({
  variant: "tertiary"
})`
  height: 2.2rem;
  width: 2.2rem;
  ${marginLeft.s}
  ${marginBottom.m}
`;
const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  left: 0.625rem;
  top: 2.6rem;
`;
const UseLocationIcon = styled(FontAwesomeIcon)``;
const SearchButton = styled(Button)`
  width: 100%;
`;
const NoResultMessage = styled.div`
  ${marginTop.m}
  ${marginBottom.m}
`;